import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@ordo/ui/accordion'
import { badgeVariants } from '@ordo/ui/badge'
import { cn } from '@ordo/ui/cn'
import { Shuffle } from 'lucide-react'

import { IngredientBrandPicker } from '@/components/chefs/ingredient-brand-picker'
import { IngredientSubstitutionDisplay } from '@/components/chefs/ingredient-substitution-display'
import {
  type CapAdvisory,
  capAdvisoryForAmount,
  displayAmount,
  type MeasurementDisplay,
  type UnitSystem,
} from '@/lib/chefs/recipe-quantities'
import type { Component, Ingredient, IngredientRef, Recipe } from '@/lib/chefs/types'

/** Scaling context from the detail panel's multiplier + unit dropdowns. */
export interface RecipeScale {
  /** Serving count the chef is prepping (the "200x" multiplier). */
  servings: number
  system: UnitSystem
  /** Kitchen-wide Standard | Technical reading. Default standard. */
  mode?: MeasurementDisplay
}

interface RecipeDetailSectionsProps {
  recipe: Recipe | undefined
  emptyMessage: string
  /** When set, amounts scale to `servings` and convert into `system`.
   *  When absent, authored amounts render as-is (recipes drawer, print). */
  scale?: RecipeScale
  /** The shipped serving over the recipe's PRIMARY serving. Amounts are
   *  authored for the primary, so a non-primary portion scales by this. */
  servingMultiplier?: number
  onSubstituteIngredient?: () => void
  className?: string
}

interface ComponentRecipeBlockProps {
  component: Component
  scale?: RecipeScale
  onSubstituteIngredient?: () => void
}

/** Display label for one amount, or null → the name renders alone (never `0 unit`). */
function amountLabel(
  amount: Pick<IngredientRef, 'quantity' | 'unit' | 'quantityBasis'>,
  recipe: Recipe,
  scale: RecipeScale | undefined,
  servingMultiplier?: number,
): string | null {
  if (amount.quantity === null || amount.unit === null) {
    return null
  }
  if (!scale) {
    return `${amount.quantity} ${amount.unit}`
  }
  return displayAmount(amount, {
    servings: scale.servings,
    baseYield: recipe.baseYield,
    servingMultiplier,
    system: scale.system,
    mode: scale.mode,
  })
}

/**
 * The scaling-cap advisory for one ingredient row, or null.
 *
 * Only meaningful once a scale is applied — an unscaled row shows the authored
 * amount, which is what the cap is authored against. Advisory only: see
 * `capAdvisory`. The cap reads "per batch / roasting pan" and nothing here
 * knows the pan count, so it is surfaced for the chef to judge, never enforced.
 */
function capFor(
  ing: Ingredient,
  recipe: Recipe,
  scale: RecipeScale | undefined,
  servingMultiplier?: number,
): CapAdvisory | null {
  if (!scale) {
    return null
  }
  // Judge the cap against the portion actually plated. Without the multiplier
  // a 1/4-cup serving is measured as a full primary one, so a reduced portion
  // would raise a cap warning for food the kitchen never makes.
  return capAdvisoryForAmount(ing, {
    servings: scale.servings,
    baseYield: recipe.baseYield,
    servingMultiplier,
    system: scale.system,
    mode: scale.mode,
  })
}

/** Warning chip + the author's note, shown UNDER the amount it refers to. */
function ScalingCapAdvisory({ advisory, ingredientName }: { advisory: CapAdvisory; ingredientName: string }) {
  return (
    <span className="flex flex-col gap-0.5 pt-0.5">
      <span
        className={cn(badgeVariants({ variant: 'warning' }), 'w-fit')}
        role="note"
        aria-label={`${ingredientName} exceeds its scaling cap of ${advisory.capLabel}`}>
        {`Scaling cap ${advisory.capLabel} per batch`}
      </span>
      <span className="text-xs text-muted-foreground">
        {advisory.notes ?? 'Does not scale linearly — check the batch ratio before prepping.'}
      </span>
    </span>
  )
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Accordion type="single" collapsible defaultValue={title} className="w-full">
      <AccordionItem value={title} className="border-b-0">
        <AccordionTrigger className="rounded-md bg-muted px-3 py-2 text-sm font-medium hover:no-underline data-[state=open]:rounded-b-none">
          {title}
        </AccordionTrigger>
        <AccordionContent className="px-2.5 pb-2 pt-3">{children}</AccordionContent>
      </AccordionItem>
    </Accordion>
  )
}

export function RecipeDetailSections({
  recipe,
  emptyMessage,
  scale,
  servingMultiplier,
  onSubstituteIngredient,
  className,
}: RecipeDetailSectionsProps) {
  if (!recipe) {
    return <p className={cn('text-sm text-muted-foreground', className)}>{emptyMessage}</p>
  }

  return (
    <div className={cn('space-y-3', className)}>
      <Section title="Ingredients">
        {recipe.ingredients.length > 0 ? (
          <div className="flex flex-col">
            {recipe.ingredients.map((ing) => {
              const label = amountLabel(ing, recipe, scale, servingMultiplier)
              const cap = capFor(ing, recipe, scale, servingMultiplier)
              return (
                <div
                  key={ing.id}
                  className="flex items-baseline gap-3 border-b border-border py-2 text-sm last:border-b-0">
                  <span className="min-w-14 shrink-0 font-medium text-foreground">
                    {label ?? <span aria-label="No amount on file">—</span>}
                  </span>
                  <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                    <IngredientSubstitutionDisplay ingredientName={ing.name} ingredientUnit={ing.unit ?? ''} />
                    {/* Choosing among an ingredient's APPROVED brands is not a
                        substitution — it applies immediately, no approval. */}
                    <IngredientBrandPicker ingredient={ing} />
                    {cap ? <ScalingCapAdvisory advisory={cap} ingredientName={ing.name} /> : null}
                  </span>
                </div>
              )
            })}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">No ingredients have been entered for this recipe yet.</p>
        )}
        {onSubstituteIngredient ? (
          <button
            type="button"
            onClick={onSubstituteIngredient}
            className="mt-2 inline-flex items-center gap-1.5 text-xs font-medium text-primary hover:underline">
            <Shuffle className="h-3.5 w-3.5" strokeWidth={1.75} />
            Need to substitute an ingredient?
          </button>
        ) : null}
      </Section>

      <Section title="Recipe">
        {recipe.steps.length > 0 ? (
          <ol className="flex flex-col gap-2.5">
            {recipe.steps.map((step, i) => (
              <li key={step.id} className="flex gap-3 text-sm">
                <span className="w-6 shrink-0 text-base font-medium text-foreground">{i + 1}</span>
                <div className="flex flex-1 flex-wrap items-baseline gap-x-2 gap-y-1">
                  <span className="text-foreground">{step.text}</span>
                  {step.ingredientRefs.map((ref, refIdx) => {
                    const label = amountLabel(ref, recipe, scale, servingMultiplier)
                    return (
                      <span
                        key={refIdx}
                        className="inline-flex items-baseline gap-1 rounded-full border border-border px-2 text-xs text-muted-foreground">
                        {label ? <span>{label}</span> : null}
                        <span>{ref.name}</span>
                      </span>
                    )
                  })}
                </div>
              </li>
            ))}
          </ol>
        ) : (
          <p className="text-sm text-muted-foreground">No preparation steps have been entered for this recipe yet.</p>
        )}
      </Section>

      <Section title="HACCP Points">
        {recipe.haccp.length > 0 ? (
          <ol className="flex flex-col gap-2.5">
            {recipe.haccp.map((p, i) => (
              <li key={p.id} className="flex gap-3 text-sm">
                <span className="w-6 shrink-0 text-base font-medium text-foreground">{i + 1}</span>
                <div className="flex flex-1 flex-wrap items-baseline gap-x-2 gap-y-1">
                  <span className="text-foreground">{p.text}</span>
                  {p.ingredientRef ? (
                    <span className="inline-flex items-baseline gap-1 border-b border-border px-1 text-xs text-muted-foreground">
                      {amountLabel(p.ingredientRef, recipe, scale, servingMultiplier) ? (
                        <span>{amountLabel(p.ingredientRef, recipe, scale, servingMultiplier)}</span>
                      ) : null}
                      <span>{p.ingredientRef.name}</span>
                    </span>
                  ) : null}
                </div>
              </li>
            ))}
          </ol>
        ) : (
          <p className="text-sm text-muted-foreground">No HACCP points have been entered for this recipe yet.</p>
        )}
      </Section>

      {recipe.tips && recipe.tips.length > 0 ? (
        <Section title="Tips">
          <ul className="flex flex-col gap-1.5 text-sm text-muted-foreground">
            {recipe.tips.map((t) => (
              <li key={t.id}>· {t.text}</li>
            ))}
          </ul>
        </Section>
      ) : null}
    </div>
  )
}

export function ComponentRecipeBlock({ component, scale, onSubstituteIngredient }: ComponentRecipeBlockProps) {
  // A condiment with NO recipe on file (a packet of ketchup) is a pack count,
  // not something the kitchen makes — it stays the compact ×N row. A
  // house-made condiment (ranch, salsa, taco sauce) carries a recipe the
  // kitchen has to batch, so it renders the full sections with the per-meal
  // pack count kept above them.
  if (component.isCondiment && !component.recipe) {
    return (
      <div className="flex items-center justify-between text-base">
        <span className="font-medium">{component.name}</span>
        <span className="text-sm text-muted-foreground">×{component.condimentQty ?? 1}</span>
      </div>
    )
  }

  return (
    <>
      {component.isCondiment && component.condimentQty !== undefined ? (
        <div className="flex items-center justify-between pb-2 text-sm text-muted-foreground">
          <span>Per meal</span>
          <span>×{component.condimentQty}</span>
        </div>
      ) : null}
      <RecipeDetailSections
        recipe={component.recipe}
        emptyMessage="No recipe on file for this component."
        scale={scale}
        servingMultiplier={component.servingMultiplier}
        onSubstituteIngredient={onSubstituteIngredient}
      />
    </>
  )
}
